import React, { useEffect, useMemo } from "react";
import { Modal } from "antd";
// import { Button, Progress } from "@digitallabs/one-x-ui";
import { Button, Progress } from "../../resourses/digitallabs/one-x-ui";
import "../../styles/SplashScreenModal.css";
import { DMQStepNames, Questionnaires } from "../../constants/constants";
import { useLoader } from "../../context/LoaderContext";
import { getQuestionSet } from "../../services/serviceCollections/dmqService";
import { showCatchError } from "../../utils/toastMessageUtil";
import { getQuestionBank, getStoredFormData, setQuestionBank } from "../../utils/formStorage";
import { buildStepFlow } from "../../utils/stepFlowManager";
import { isStepCompleted } from "./CustomStepper";

export const CONDITION_TO_QSET = [
  {
    condition: "isUserNRI",
    qid: Questionnaires.NRI_QUESTIONNAIRE,
  },
  {
    condition: "isUserFN",
    qid: Questionnaires.FOREIGN_NATIONAL_QUESTIONNAIRE,
  },
  {
    condition: "isDiabetic",
    qid: Questionnaires.DIABETIC_QUESTIONNAIRE,
  },
  {
    condition: "isBloodPressure",
    qid: Questionnaires.BLOOD_PRESSURE_QUESTIONNAIRE,
  },
  {
    condition: "isThyroid",
    qid: Questionnaires.THYROID_QUESTIONNAIRE,
  },
  {
    condition: "isKidney",
    qid: Questionnaires.KIDNEY_QUESTIONNAIRE,
  },
];

export const appendQuestionBank = (newContent) => {
  if (typeof window === "undefined") return false;

  try {
    const existing = getQuestionBank();

    // Merge entire content dynamically
    const updatedData = {
      ...existing,
      ...newContent,
    };

    setQuestionBank(updatedData);

    return true;
  } catch (error) {
    console.log("Error appending question set", error);
    return false;
  }
};

const SplashScreenModal = ({ visible, conditions, onContinue }) => {
  const { setLoading } = useLoader();
  const stored = getStoredFormData();

  useEffect(() => {
    if (visible) {
      populateQuestionnaires();
    }
  }, [visible]);

  const populateQuestionnaires = async () => {
    setLoading(true);

    try {
      const existing = getQuestionBank();

      //  Filter only required questionnaires
      const requiredQSets = CONDITION_TO_QSET.filter(({ condition }) => conditions[condition]);

      //  Avoid duplicate API calls
      const qSetsToFetch = requiredQSets.filter(({ qid }) => {
        // Check if already present in localStorage
        return !Object.values(existing).some((category) => category?.some?.((q) => q?.question_id === qid));
      });

      //  API calls in parallel
      const responses = await Promise.all(qSetsToFetch.map(({ qid }) => getQuestionSet({ q_set_id: qid })));

      //  Merge all responses
      let combinedContent = {};

      responses.forEach((res) => {
        const content = res?.data?.content;
        combinedContent = {
          ...combinedContent,
          ...content,
        };
      });

      appendQuestionBank(combinedContent);
    } catch (error) {
      console.log("Error on get question bank", error);
      showCatchError(error.message || error);
    } finally {
      setLoading(false);
    }
  };

  const progressValue = () => {
    const steps = buildStepFlow(
      conditions.isUserNRI,
      conditions.isUserFN,
      conditions.isDiabetic,
      conditions.isBloodPressure,
      conditions.isThyroid,
      conditions.isKidney
    );

    console.log("Steps for progress calculation", steps);

    let completedCount = 0;

    steps.forEach((step) => {
      const isCompleted = isStepCompleted(step.id, stored);

      if (isCompleted) {
        completedCount++;
      }
    });

    const totalSteps = steps.length;

    if (totalSteps === 0) return 0;

    return Math.round((completedCount / totalSteps) * 100);
  };

  return (
    <Modal open={visible} footer={null} closable={false} centered width={500} maskClosable={false} styles={{ body: { height: "80vh" } }}>
      <div className="modal-container">
        {/* <div className="modal-bg-blur" /> */}

        <div className="modal-content">
          <div className="modal-top">
            <p className="modal-title">Great progress so far!</p>

            <Progress color="red" max={100} min={0} type="bar" value={progressValue()} />

            <p className="modal-sub-title">We just have a few more questions based on your responses.</p>

            <p className="modal-sub-title">Please answer the following additional questionnaires to proceed further</p>

            <ul className="custom-list">
              {conditions.isUserNRI && <li>{DMQStepNames.NRI_QUESTIONNAIRE}</li>}
              {conditions.isUserFN && <li>{DMQStepNames.FOREIGN_NATIONAL_QUESTIONNAIRE}</li>}
              {conditions.isDiabetic && <li>{DMQStepNames.DIABETIC_QUESTIONNAIRE}</li>}
              {conditions.isBloodPressure && <li>{DMQStepNames.BLOOD_PRESSURE_QUESTIONNAIRE}</li>}
              {conditions.isThyroid && <li>{DMQStepNames.THYROID_QUESTIONNAIRE}</li>}
              {conditions.isKidney && <li>{DMQStepNames.KIDNEY_QUESTIONNAIRE}</li>}
            </ul>
          </div>

          <div className="modal-bottom">
            <p className="modal-sub-title">Keeping your doctor’s reports handy will help you continue smoothly</p>

            <div style={{ marginTop: "20px", marginBottom: "10px" }}>
              <Button variant="primary" size="md" fullWidth onClick={onContinue}>
                Continue
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SplashScreenModal;
